import { useEffect, useState } from "react";
import { Plus, Trash2, Pencil, Shield } from "lucide-react";
import { api, roleLabel, initials, avatarColor } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function UsersPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [regions, setRegions] = useState([]);
  const [rcsms, setRcsms] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => {
    api.get("/users").then((r) => setUsers(r.data));
    api.get("/roles").then((r) => setRoles(r.data));
  };
  useEffect(() => {
    load();
    api.get("/regions").then((r) => setRegions(r.data));
    api.get("/users", { params: { role: "rcsm" } }).then((r) => setRcsms(r.data));
  }, []);

  const del = async (id) => { await api.delete(`/users/${id}`); toast.success("User removed"); load(); };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">Users & Roles</h1>
          <p className="mt-1 text-sm text-slate-500">Manage system users and their access.</p>
        </div>
        <Button data-testid="add-user-button" onClick={() => { setEditing(null); setOpen(true); }} className="bg-orange-500 hover:bg-orange-600">
          <Plus className="mr-1.5 h-4 w-4" /> Add user
        </Button>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        <table data-testid="users-table" className="w-full text-sm">
          <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Role</th>
              <th className="px-4 py-3">Regions</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} data-testid="user-row" className="border-b border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold ${avatarColor(u.name)}`}>{initials(u.name)}</div>
                    <span className="font-medium text-slate-900">{u.name}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-slate-600">{u.email}</td>
                <td className="px-4 py-3"><span className="rounded-full bg-slate-900 px-2 py-0.5 text-xs font-medium text-white">{roleLabel(u.role)}</span></td>
                <td className="px-4 py-3 text-slate-600">{(u.region_ids || []).length}</td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-1">
                    <button onClick={() => { setEditing(u); setOpen(true); }} className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><Pencil className="h-4 w-4" /></button>
                    {u.id !== user.id && user.role === "admin" && (
                      <button onClick={() => del(u.id)} className="rounded p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <UserDialog open={open} onOpenChange={setOpen} editing={editing} roles={roles} regions={regions} rcsms={rcsms} onSaved={load} />
    </div>
  );
}

function UserDialog({ open, onOpenChange, editing, roles, regions, rcsms, onSaved }) {
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "rcsm", region_ids: [], rcsm_id: "" });

  useEffect(() => {
    if (!open) return;
    setForm(editing
      ? { name: editing.name, email: editing.email, password: "", role: editing.role, region_ids: editing.region_ids || [], rcsm_id: editing.rcsm_id || "" }
      : { name: "", email: "", password: "", role: "rcsm", region_ids: [], rcsm_id: "" });
  }, [open, editing]);

  const toggleRegion = (id) =>
    setForm((f) => ({ ...f, region_ids: f.region_ids.includes(id) ? f.region_ids.filter((x) => x !== id) : [...f.region_ids, id] }));

  const save = async () => {
    if (!form.name || !form.email || (!editing && !form.password)) { toast.error("Name, email and password required"); return; }
    try {
      const payload = { ...form, rcsm_id: form.rcsm_id || null };
      if (editing) {
        const upd = { name: form.name, role: form.role, region_ids: form.region_ids, rcsm_id: payload.rcsm_id };
        if (form.password) upd.password = form.password;
        await api.put(`/users/${editing.id}`, upd);
      } else {
        await api.post("/users", payload);
      }
      toast.success(editing ? "User updated" : "User created");
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save user");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="user-dialog">
        <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit user" : "Add user"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase text-slate-500">Full name</Label>
            <Input className="mt-1.5" data-testid="user-name-input" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          </div>
          <div>
            <Label className="text-xs uppercase text-slate-500">Email</Label>
            <Input className="mt-1.5" data-testid="user-email-input" disabled={!!editing} value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
          </div>
          <div>
            <Label className="text-xs uppercase text-slate-500">{editing ? "New password (optional)" : "Password"}</Label>
            <Input className="mt-1.5" type="password" data-testid="user-password-input" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} />
          </div>
          <div>
            <Label className="text-xs uppercase text-slate-500">Role</Label>
            <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
              <SelectTrigger className="mt-1.5" data-testid="user-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>{roles.map((r) => <SelectItem key={r} value={r}>{roleLabel(r)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {(form.role === "rcsm" || form.role === "cnat") && (
            <div>
              <Label className="text-xs uppercase text-slate-500">Assigned regions</Label>
              <div className="mt-1.5 flex flex-wrap gap-2">
                {regions.map((r) => (
                  <button type="button" key={r.id} onClick={() => toggleRegion(r.id)}
                    className={`rounded-full border px-3 py-1 text-sm ${form.region_ids.includes(r.id) ? "border-orange-500 bg-orange-50 text-orange-700" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                    {r.name}
                  </button>
                ))}
              </div>
            </div>
          )}
          {form.role === "cnat" && (
            <div>
              <Label className="text-xs uppercase text-slate-500">Allocated RCSM</Label>
              <Select value={form.rcsm_id} onValueChange={(v) => setForm((f) => ({ ...f, rcsm_id: v }))}>
                <SelectTrigger className="mt-1.5" data-testid="user-rcsm-select"><SelectValue placeholder="Select RCSM" /></SelectTrigger>
                <SelectContent>{rcsms.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button data-testid="user-save-button" onClick={save} className="bg-orange-500 hover:bg-orange-600">Save</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
